import clsx from 'clsx';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';

import Checkbox from '../../Checkbox/Checkbox';
import DateRange from '../DateRange/DateRange';
import Sort from '../Sort/Sort';
import TextContaining from '../TextContaining/TextContaining';

export enum FilterBoxTypes {
  TextContaining = 'textContaining',
  DateRange = 'dateRange',
  Checkbox = 'checkbox',
  Boolean = 'boolean',
}

type BaseFilterOption = {
  id: string;
  isActive: boolean;
};

export type CheckboxFilterOption = BaseFilterOption & {
  type: FilterBoxTypes.Checkbox;
  value: (BaseFilterOption & {
    key: string;
    label: string;
    value: string;
  })[];
};

export type DateRangeFilterOption = BaseFilterOption & {
  type: FilterBoxTypes.DateRange;
  value: {
    from: string;
    to: string;
  };
};

export type TextContainingFilterOption = BaseFilterOption & {
  type: FilterBoxTypes.TextContaining;
  value: string;
};

export type BooleanFilterOption = BaseFilterOption & {
  type: FilterBoxTypes.Boolean;
  value: boolean;
};

export type FilterOption =
  | CheckboxFilterOption
  | DateRangeFilterOption
  | TextContainingFilterOption
  | BooleanFilterOption;

const FilterBox = (props) => {
  const [searchValue, setSearchValue] = useState(null);
  const [searchedFilterOptions, setSearchedFilterOptions] = useState([]);

  const debouncedHandleSearch = _.debounce((searchValue) => {
    const { filterOptions, onSearchFilters } = props;
    setSearchValue(searchValue);
    setSearchedFilterOptions(
      filterOptions.filter((f) => f.label.toLowerCase().includes(searchValue))
    );
    if (onSearchFilters) {
      onSearchFilters(searchValue);
    }
  }, 400);

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    debouncedHandleSearch(searchValue);
  };

  useEffect(() => {
    return debouncedHandleSearch.cancel;
  }, [debouncedHandleSearch]);

  const {
    column,
    filterOptions,
    filterSelected,
    isSearchable = false,
    classes = {},
    excludeFilter,
    sortingType,
    sortingSelected,
    onUpdateSorting,
    onUpdateFilters,
    disableFilters,
    hideFilterSection,
    selectAllLabel,
    bottomContent,
  } = props;

  const options = searchValue ? searchedFilterOptions : filterOptions;
  const isAllOptionVisible = !searchValue;

  let isCheckedAll =
    filterSelected && filterSelected.filter((f) => f.isActive).length === options.length;
  if (excludeFilter) {
    isCheckedAll =
      filterSelected &&
      filterSelected.filter((f) => f.isActive && f.id !== excludeFilter).length > 0 &&
      filterSelected.filter((f) => f.isActive && f.id !== excludeFilter).length ===
        options.filter((f) => f.id !== excludeFilter).length;
  }

  const isFilterChecked = (value) => {
    if (filterSelected) {
      const index = filterSelected.map((f) => f.id).indexOf(value);
      if (index > -1) {
        return filterSelected[index].isActive;
      }
    }
    return false;
  };

  const handleSelectAll = () => {
    if (isCheckedAll) {
      onUpdateFilters(filterSelected.map((f) => ({ ...f, isActive: false })));
    } else if (!isCheckedAll) {
      onUpdateFilters(filterSelected.map((f) => ({ ...f, isActive: true, value: '' })));
    }
  };

  const handleSelectFilters = (value) => {
    if (isFilterChecked(value)) {
      onUpdateFilters([
        ...(filterSelected || []).map((f) => {
          if (f.id === value) {
            return { ...f, isActive: false };
          }
          return f;
        }),
      ]);
    } else {
      onUpdateFilters([
        ...(filterSelected || []).map((f) => {
          if (f.id === value) {
            return { ...f, isActive: true, value: '' };
          }
          return f;
        }),
      ]);
    }
  };

  return (
    <div className={clsx(`filterBoxContainer`, classes.options)}>
      <Sort
        column={column}
        sortingType={sortingType}
        sortingSelected={sortingSelected}
        onUpdateSorting={onUpdateSorting}
      />
      {!hideFilterSection && (
        <div
          className={clsx(
            `filterByContainer`,
            options.filter((f) => f.id === 'dateRange').length > 0 && 'dateRangeBoxContainer',
            classes.filterBy
          )}
        >
          <h4>Filter By</h4>
          {isSearchable && (
            <div className="searchItems">
              <input type="text" placeholder="Search" onChange={handleSearch} />
              <i className="material-icons">search</i>
            </div>
          )}
          <div className="filterItems">
            {searchValue && searchedFilterOptions.length < 1 && (
              <div className="filterItem no-results-found">No Results Found</div>
            )}
            {isAllOptionVisible && (
              <div className={clsx(`filterItem`, disableFilters && 'disabled')}>
                <div className="filterCheckbox">
                  <Checkbox value="all" onChange={handleSelectAll} checked={isCheckedAll} />
                  <span>{selectAllLabel ? selectAllLabel : 'All'}</span>
                </div>
              </div>
            )}

            {/* Depending on filterSelected.type, conditionally set it here */}
            {options.length > 0 &&
              options.map((filter) => {
                if (filter.option) {
                  if (filter.option === 'textContaining') {
                    return (
                      <div key={`textContainingFilterKey`} className="filterItem">
                        <TextContaining
                          filter={filter}
                          filterSelected={props.filterSelected}
                          onUpdate={onUpdateFilters}
                          onSelect={handleSelectFilters}
                          isChecked={isFilterChecked(filter.id)}
                        />
                      </div>
                    );
                  } else if (filter.option === 'dateRange') {
                    return (
                      <div key={`dateRangeFilterKey`} className="filterItem">
                        <DateRange
                          filter={filter}
                          filterSelected={props.filterSelected}
                          onUpdate={onUpdateFilters}
                          onSelect={handleSelectFilters}
                          isChecked={isFilterChecked(filter.id)}
                        />
                      </div>
                    );
                  }
                }
                return (
                  <div
                    key={`filterItemKey_${filter.id}`}
                    className={clsx(`filterItem`, filter.class, disableFilters && 'disabled')}
                  >
                    <div className="filterCheckbox">
                      <Checkbox
                        value={filter.id}
                        onChange={handleSelectFilters}
                        checked={isFilterChecked(filter.id)}
                      />
                      <span>{filter.label}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
      <div className="bottomContent">{bottomContent}</div>
    </div>
  );
};

FilterBox.defaultProps = {
  filters: [],
  classes: {},
};

export { FilterBox };
